import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { hideModal } from "../../../actions/actionCreators";
import withModal from "../withModal";
import useStyles from "../../wiki/styles";
import MaterialModal from "../MaterialModal";
import Skeleton from "@material-ui/lab/Skeleton";
import InputLabel from "@material-ui/core/InputLabel";
import Card from "@material-ui/core/Card";
import CardHeader from "@material-ui/core/CardHeader";
import CardMedia from "@material-ui/core/CardMedia";
import CardContent from "@material-ui/core/CardContent";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import FormHelperText from "@material-ui/core/FormHelperText";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import { getImagePreview, SetWikiPreview, addWikiImage } from "../../../actions/wikiActions";
import { IAppState } from "../../../types/stores/index";

type Props = {
    isOpen: boolean;
    modalProps: any;
};

const ImagesModal: React.FC<Props> = ({ isOpen }: Props) => {
    const dispatch = useDispatch();
    const classes = useStyles();

    const [selectedImageName, setSelectedImageName] = useState("");
    const [selectedImageContent, setSelectedImageContent] = useState("");
    const [selectedImageTitleOverride, setSelectedImageTitleOverride] = useState("");
    const [selectedImageCategory, setSelectedImageCategory] = useState(1);

    const isImageSaving = useSelector((state: IAppState) => state.wiki.isSaving);
    const isPreviewLoading = useSelector((state: IAppState) => state.wiki.isLoadingPreview);
    const selectedImagePreview = useSelector((state: IAppState) => state.wiki.selectedImagePreview);

    const handleImageTitle = event => {
        const parsedVal = event.target.value.replace(/[^A-Z0-9]+/gi, "-").toLowerCase();
        setSelectedImageTitleOverride(parsedVal);
    };

    const handleSelectedImageCategory = (e: any) =>
        e.target && e.target.value ? setSelectedImageCategory(e.target.value) : null;

    const onChangeHandlerForImage = event => {
        const imageContent = event.target.files[0];
        const fileName = event.target.files[0].name;
        setSelectedImageName(fileName);
        setSelectedImageContent(imageContent);

        // set image preview
        const previewData = new FormData();
        previewData.append("Image", imageContent);

        dispatch(getImagePreview(previewData));
    };

    const save = () => {
        dispatch(addWikiImage(selectedImageContent, selectedImageTitleOverride, selectedImageCategory));
        setSelectedImageName("");
        setSelectedImageContent("");
        dispatch(SetWikiPreview(""));
        setSelectedImageTitleOverride("");
        setSelectedImageCategory(1);
        dispatch(hideModal("IMAGES_MODAL"));
    };

    return (
        <MaterialModal
            id="wiki-images-modal"
            isOpen={isOpen}
            title="Add New Image"
            innerDescription="Find and add a new image to add later into your page editor"
            isPrimaryButtonDisabled={
                !selectedImageName ||
                !selectedImageContent ||
                isImageSaving ||
                isPreviewLoading ||
                !selectedImageTitleOverride
            } // todo: disable if there aren't any new images
            onClose={() => {
                dispatch(hideModal("IMAGES_MODAL"));
                setSelectedImageName("");
                setSelectedImageContent("");
                dispatch(SetWikiPreview(""));
                setSelectedImageTitleOverride("");
                setSelectedImageCategory(1);
            }}
            onPrimaryButtonRequest={() => save()}
            primaryButtonText="Save New Image"
        >
            <div className="wiki-images-modal-container">
                <button
                    className="material-button transparent"
                    style={{ width: "100%", margin: "10px auto" }}
                    onClick={() => {
                        const uploadLogo: HTMLElement | null = document.getElementById("uploadLogo");
                        return uploadLogo ? uploadLogo.click() : null;
                    }}
                >
                    <i className="material-icons">search</i>
                    &nbsp; Browse For New Image
                    <input
                        id="uploadLogo"
                        type="file"
                        name="file"
                        accept=".png, .jpg, .jpeg"
                        style={{ display: "none" }}
                        onChange={e => onChangeHandlerForImage(e)}
                    />
                </button>
                {selectedImageName && (
                    <Card className={"wiki-image-preview-inner"}>
                        {!isPreviewLoading && selectedImagePreview ? (
                            <>
                                <CardHeader title={`Preview for: ${selectedImageName}`} />
                                <CardMedia image={selectedImagePreview} title={selectedImageName} />
                                <CardContent>
                                    <div style={{ display: "flex" }}>
                                        <TextField
                                            className={classes.title}
                                            required
                                            id="image-title-override"
                                            label="Image Name"
                                            helperText="Reference name for the editor. e.g. my-image"
                                            value={selectedImageTitleOverride}
                                            onChange={handleImageTitle}
                                        />
                                        <FormControl className={classes.formControl}>
                                            <InputLabel id="image-category">Category</InputLabel>
                                            <Select
                                                labelId="image-category"
                                                value={selectedImageCategory}
                                                onChange={e => handleSelectedImageCategory(e)}
                                            >
                                                <MenuItem value={1}>None</MenuItem>
                                                <MenuItem value={2}>Flows</MenuItem>
                                                <MenuItem value={3}>Release</MenuItem>
                                            </Select>
                                            <FormHelperText>
                                                Mostly for organization when viewing images.
                                            </FormHelperText>
                                        </FormControl>
                                    </div>
                                </CardContent>
                            </>
                        ) : (
                            <div style={{ display: "flex", flexDirection: "column", padding: "20px" }}>
                                <Skeleton animation="wave" height={10} style={{ marginBottom: 6 }} />
                                <Skeleton animation="wave" variant="rect" style={{ height: "200px" }} />
                            </div>
                        )}
                    </Card>
                )}
            </div>
        </MaterialModal>
    );
};

export default withModal(ImagesModal, "IMAGES_MODAL");
